/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from "react";
import { discovery, router, auth2 as auth } from "dc-core";
import {
  modernViewerDropinContext,
  initialQTOrderedVerbs,
  quickToolsDropinIdsForFillSign,
  quickToolsVerbIdsForCommenting,
  featureLifecyleHooks,
  quickToolsVerbIdsForMarkupAnnots
} from "constants/enums";
import CommonDialog from "dc-preview-utils/src/components/dialog/CommonDialog";
import localizeComponent from "dc-preview-utils/src/Utils/LocalizationUtil";
import loadTranslation from "utils/TranslationUtil";
import { FormattedMessage } from "react-intl";
import { dialogApi } from "components/dialogs";
import {
  getFileExtension,
  getShouldShowCommentingTools,
  getIsImageCurrentSelection
} from "selectors/documentSelectors";
import { MEGAVERB_ID_CONVERT, ALL_QUICKTOOLS } from "../constants/constants";
import { generateRedirectURL } from "../utils/UrlUtils";
import * as stores from "stores";
import { trackQuickToolVerb } from "analytics/events";
import { getConnectorType } from "app/AppInitUtility";
import { isBoolean } from "utils/lodashUtils";
import { action, runInAction } from "mobx";
import { getShouldRenderFillSignDropin } from "selectors/overlayDropinSelectors";
import { recordDropinQuickToolEnabledTime } from "utils/performance";
import {
  getActiveVerb,
  getQuickToolsVerbs,
  getVerbGroupState,
  getGroupIndexToShowFlyout,
  getDisabledQuickToolsVerbs as getDisabledVerbs,
  getVerbsAtIndex
} from "selectors/quickToolsSelectors";
import { featureLifecycleHookController } from "publicApis/controller/featureLifecycleHookController";
import { getShouldUseMobileModernViewer } from "selectors/appSelectors";
import { isThereAnySelection } from "dc-preview-utils/src/selectors/touchSelectors";
import { pageContentHandler } from "pageContentHandler";
import { getZoomedQuadsObj } from "utils/SelectionUtils";
import { getContextMenuTriggerPageIndex } from "selectors/contextMenuSelectors";

const getFormattedMessage = (id, values) => <FormattedMessage id={id} values={values} />;

const callDropinMethod = (dropinId, methodName, opts) => discovery.callBootstrapMethod(dropinId, methodName, opts);

export const callInitializeAction = (dropinId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(dropinId, "initializeAction", { ...opts, context: modernViewerDropinContext.QUICK_TOOLS }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const fetchVerbIds = (dropinId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(dropinId, "getQuickTools", { ...opts, context: modernViewerDropinContext.QUICK_TOOLS }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const fetchVerbIcon = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getIcon", { ...opts, context: modernViewerDropinContext.QUICK_TOOLS }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const fetchVerbLabel = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getLabel", { ...opts, context: modernViewerDropinContext.QUICK_TOOLS }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const fetchVerbAriaLabel = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getAriaLabel", { ...opts, context: modernViewerDropinContext.QUICK_TOOLS }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const fetchTooltipInfo = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getTooltipInfo", { ...opts, context: modernViewerDropinContext.QUICK_TOOLS }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const fetchVerbTooltip = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getTooltip", { ...opts, context: modernViewerDropinContext.QUICK_TOOLS }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const callExitCallback = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "exitCallback", { ...opts, context: modernViewerDropinContext.QUICK_TOOLS }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const callExecuteAction = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "executeAction", { ...opts, context: modernViewerDropinContext.QUICK_TOOLS }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const callGetComponent = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getComponent", { ...opts, context: modernViewerDropinContext.QUICK_TOOLS }).then(
      resp => {
        resolve(resp);
      },
      err => {
        resolve(null);
      }
    );
  });

export const fetchFileSelectionParams = (verbId, opts = {}) =>
  new Promise((resolve, reject) => {
    callDropinMethod(verbId, "getFileSelectionParams", {
      ...opts,
      context: modernViewerDropinContext.QUICK_TOOLS
    }).then(
      resp => {
        resolve(resp);
      },
      err => {
        reject(err);
      }
    );
  });

export const isIdValid = (idsList, Id) => Object.keys(idsList).some(key => idsList[key] === Id);

const isDisabled = action(verb => (disabled, notificationMsg) => {
  const fileExtension = getFileExtension(stores).toLowerCase();
  fetchFileSelectionParams(verb).then(fileSelectionParams => {
    // Don't Disable QuickTools for Unsupported FileExtensions.
    if (fileSelectionParams && fileExtension && !fileSelectionParams.acceptExtensions.includes(fileExtension)) {
      stores.quickToolsStore.updatePropertyForVerb(verb, "disabled", false);
      recordDropinQuickToolEnabledTime(verb);
    } else if (isBoolean(disabled)) {
      stores.quickToolsStore.updatePropertyForVerb(verb, "disabled", disabled);
      stores.quickToolsStore.updatePropertyForVerb(verb, "disabledStateNotificationMsg", notificationMsg);
      if (!disabled) {
        recordDropinQuickToolEnabledTime(verb);
      }
    }
  });
});

const enableQuickTool = async verb => {
  const connectorAsset = getConnectorType();
  try {
    const fileSelectionParams = await fetchFileSelectionParams(verb);
    if (
      fileSelectionParams &&
      fileSelectionParams.acceptConnectors &&
      fileSelectionParams.acceptConnectors.length > 0
    ) {
      const connectorsList = fileSelectionParams.acceptConnectors;
      if (connectorsList === ALL_QUICKTOOLS) {
        return true;
      }
      return connectorsList.find(connector => connector === connectorAsset);
    }
  } catch (e) {
    return false;
  }
  return false;
};

const showVerbCallback = (verb, verbGroupIndex) => show => {
  if (isBoolean(show)) {
    runInAction(() => {
      stores.quickToolsStore.updatePropertyForVerb(verb, "show", show);
      if (show === true) {
        stores.quickToolsStore.updateActiveVerbInGroup(verb, verbGroupIndex, !getShouldUseMobileModernViewer(stores));
      }
    });
  }
};

const updateActiveVerb = (verb, opts) => {
  stores.documentStore.deRegisterSecondaryComponent();
  stores.quickToolsStore.setActiveVerb(verb, opts);
};

export const getVerbDetails = async verbId => {
  const icon = fetchVerbIcon(verbId);
  const label = fetchVerbLabel(verbId);
  const tooltip = fetchVerbTooltip(verbId);
  const ariaLabel = fetchVerbAriaLabel(verbId);

  const verbConfig = await Promise.all([icon, label, tooltip, ariaLabel]);
  return {
    verbId,
    icon: verbConfig[0],
    label: verbConfig[1],
    tooltip: verbConfig[2],
    ariaLabel: verbConfig[3]
  };
};

const getFilteredVerbs = async () => {
  const enableFillSignQuickTools = getShouldRenderFillSignDropin(stores);
  const shouldShowCommentingTools = getShouldShowCommentingTools(stores);
  const connectorAsset = getConnectorType();
  const filteredVerbs = await Promise.all(
    initialQTOrderedVerbs.map(async verb => {
      if (connectorAsset) {
        const shouldEnableTool = await enableQuickTool(verb);
        return shouldEnableTool;
      }
      if (quickToolsDropinIdsForFillSign.includes(verb)) {
        return enableFillSignQuickTools;
      }
      if (quickToolsVerbIdsForCommenting.includes(verb)) {
        return shouldShowCommentingTools;
      }
      return true;
    })
  );
  return initialQTOrderedVerbs.filter((_verb, index) => {
    stores.quickToolsStore.updatePropertyForVerbGroup(index, "isValid", filteredVerbs[index]);
    return filteredVerbs[index];
  });
};

const areGroupVerbsFetched = groupIndex => {
  const { quickToolsStore } = stores;
  return quickToolsStore.verbGroupState[groupIndex] && quickToolsStore.verbGroupState[groupIndex].groupVerbsFetched;
};

const getQtGroupVerbs = async (verb, groupIndex) => {
  const { quickToolsStore } = stores;
  if (areGroupVerbsFetched(groupIndex)) {
    return getVerbsAtIndex(stores, groupIndex);
  }
  quickToolsStore.updatePropertyForVerbGroup(groupIndex, "groupVerbsFetched", true);
  return fetchVerbIds(verb);
};

const isVerbInitialized = verbId => {
  const quickToolsVerbs = getQuickToolsVerbs(stores);
  return quickToolsVerbs[verbId] && quickToolsVerbs[verbId].initialized;
};

const isVerbDetailPresent = verbId => {
  const quickToolsVerbs = getQuickToolsVerbs(stores);
  return quickToolsVerbs[verbId] && quickToolsVerbs[verbId].label;
};

const isVerbPresentInGroup = (verbId, groupIndex) => {
  const verbGroupState = getVerbGroupState(stores);
  if (verbGroupState[groupIndex] && verbGroupState[groupIndex].verbs) {
    return verbGroupState[groupIndex].verbs.includes(verbId);
  }
  return false;
};

export const populateQTVerbGroup = async (previewConfig, force = false) => {
  const filteredVerbs = await getFilteredVerbs();
  const { quickToolsStore } = stores;
  const disabledVerbs = getDisabledVerbs(stores);
  filteredVerbs.forEach((verb, groupIndex) => {
    // Get verbs for specific group
    getQtGroupVerbs(verb, groupIndex).then(async (verbsList = []) => {
      const verbsData = await Promise.all(
        verbsList.map(qtVerb => {
          if (isVerbDetailPresent(qtVerb)) {
            return quickToolsStore.quickToolsVerbs[qtVerb];
          }
          return getVerbDetails(qtVerb);
        })
      );
      // Populate data for each verb in store
      verbsData.forEach(verbData => {
        const { verbId } = verbData;
        // Check if verb exists in store
        if (!isVerbDetailPresent(verbId)) {
          quickToolsStore.addVerbToQuickTools(verbData, groupIndex);
        }
        if (!isVerbPresentInGroup(verbId, groupIndex)) {
          quickToolsStore.updateVerbGroup(verbId, groupIndex);
        }
        if (!(isVerbInitialized(verbId) || disabledVerbs.includes(verbId)) || force) {
          callInitializeAction(verbId, {
            showCallback: showVerbCallback(verbId, groupIndex),
            isDisabled: isDisabled(verbId),
            previewConfig
          });
          quickToolsStore.updatePropertyForVerb(verbId, "initialized", true);
        }
      });
      if (verbsList.length === 1) {
        Promise.resolve(callGetComponent(verbsList[0])).then(component => {
          quickToolsStore.updatePropertyForVerbGroup(groupIndex, "Component", component);
        });
      }
    });
  });
};

const setBottomPanelVisibility = (showPrimaryPanel, showStickyPanel) => {
  if (isBoolean(showPrimaryPanel)) stores.quickToolsStore.setPrimaryPanelVisibility(showPrimaryPanel);
  if (isBoolean(showStickyPanel)) stores.documentStore.setStickyToolsVisibility(showStickyPanel);
};

export const shouldSelectVerbOnClick = (verb, quikToolIds = quickToolsVerbIdsForMarkupAnnots) =>
  !(isThereAnySelection(stores) && quikToolIds.includes(verb));
// For markup commenting, we DON'T want to select the verb if there is a text selection on document
// We will only open the flyout menu for user to select the verb options and directly create the annotation
// on selected text

const openFlyoutMenu = groupIndex => {
  stores.quickToolsStore.setGroupIndexToShowFlyout(groupIndex);
  getShouldUseMobileModernViewer(stores) && setBottomPanelVisibility(false, false);
};

// Set tooltip for flyout menu and variant when verb is selected for the guided tour through comments dropin
export const setToolTipInfoForFlyout = async (verb, toolTipInfo) => {
  stores.quickToolsStore.setTooltipConfig(verb, toolTipInfo);
  /*
    Auto hide the conditional tooltip after the specified duration
    Reset the tooltip message to the default message after the auto hide duration
   */
  setTimeout(async () => {
    resetTooltipConfig(verb);
  }, toolTipInfo.autoHideDuration);
};

export const setActiveVerbCallback = async (verb, opts = {}) => {
  const { showFlyout, triggeredFrom, shouldSelectVerbOnClick = true } = opts;
  const quickToolsVerbs = getQuickToolsVerbs(stores);
  const verbGroupState = getVerbGroupState(stores);
  const isToolSupported = await doesToolSupportFileExtension(verb);
  if (isToolSupported && quickToolsVerbs[verb]) {
    const groupIndex = quickToolsVerbs[verb].verbGroupIndex;
    const verbGroup = verbGroupState[groupIndex];
    const currentActiveVerb = verbGroup.currentActiveVerb;
    const activeVerb = getActiveVerb(stores);
    const isActiveQuickTool = currentActiveVerb === activeVerb;
    if (opts.conditionalTooltip) {
      const toolTipInfo = await fetchTooltipInfo(verb, { conditionalTooltip: opts.conditionalTooltip });
      if (toolTipInfo) {
        setToolTipInfoForFlyout(verb, toolTipInfo);
      }
    }
    if (!isActiveQuickTool || (isActiveQuickTool && currentActiveVerb !== verb)) {
      if (showFlyout && !shouldSelectVerbOnClick) {
        openFlyoutMenu(groupIndex);
        return;
      }
      featureLifecycleHookController
        .trigger(featureLifecyleHooks.BEFORE_QUICK_TOOL_ACTIVATED, verb)
        .then(() => featureLifecycleHookController.trigger(featureLifecyleHooks.SAVE_BEFORE_QUICK_TOOL_ACTIVATED, verb))
        .then(async () => {
          if (activeVerb) {
            await callExitCallback(activeVerb);
          }
          updateActiveVerb(verb, opts);
          if (!triggeredFrom || triggeredFrom === modernViewerDropinContext.QUICK_TOOLS) {
            trackQuickToolVerb(verb);
          }
          const options = {};
          if (isThereAnySelection(stores)) {
            const quadsObj = pageContentHandler.getTextManagerInstance().quads;
            if (quadsObj && quadsObj.quadsArray) {
              options.quads = getZoomedQuadsObj(quadsObj.quadsArray, stores);
            }
            options.pageIndex = getContextMenuTriggerPageIndex(stores);
            options.closeFlyout = stores.quickToolsStore.resetFlyoutIndex;
          }
          await callExecuteAction(verb, options);
          if (!shouldSelectVerbOnClick) {
            // return to previously selected verb
            setActiveVerbCallback(activeVerb);
          }
          if (showFlyout) {
            openFlyoutMenu(groupIndex);
          }
        })
        .catch(() => {});
    }
  }
};

export const quickToolClickHelper = async (event, quickToolData) => {
  const { showCaret, verbDetails, currentActiveVerb, groupIndex, shouldSelectVerbOnClick } = quickToolData;
  const isImageCurrentSelection = getIsImageCurrentSelection(stores);
  if (isImageCurrentSelection) {
    pageContentHandler.getTextManagerInstance().clearSelection(stores);
  }
  if (showCaret) {
    event.preventDefault();
    event.stopPropagation();
    // If current tool is not active
    const activeVerb = getActiveVerb(stores);
    const isActiveQuickTool = currentActiveVerb === activeVerb;
    if (!isActiveQuickTool) {
      setActiveVerbCallback(currentActiveVerb, { showFlyout: true, shouldSelectVerbOnClick });
    } else {
      if (await doesToolSupportFileExtension(currentActiveVerb)) {
        // Check if flyout is already active for current group
        const isFlyoutActiveForCurrentGroup = getGroupIndexToShowFlyout(stores) === groupIndex;
        // If flyout is already active at current group, close it
        if (isFlyoutActiveForCurrentGroup) {
          stores.quickToolsStore.resetFlyoutIndex();
        } else {
          stores.quickToolsStore.setGroupIndexToShowFlyout(groupIndex);
          getShouldUseMobileModernViewer(stores) && setBottomPanelVisibility(false, false);
        }
      }
    }
  } else {
    setActiveVerbCallback(verbDetails[0].verbId, { shouldSelectVerbOnClick });
  }
};

export const delayFunctionExecution = (callback, delay) => (...args) => {
  const timerId = setTimeout(() => {
    callback.apply(this, args);
    clearTimeout(timerId);
  }, delay);
  return timerId;
};

export const resetFlyoutIndex = () => {
  stores.quickToolsStore.resetFlyoutIndex();
};

export const setIsFocusOverQuickTool = value => {
  stores.quickToolsStore.setIsFocusOverQuickTool(value);
};

export const setIsClickedByMouse = value => {
  stores.quickToolsStore.setIsClickedByMouse(value);
};

export const resetTooltipConfig = async verb => {
  let defaultTooltipMsg;
  if (stores.quickToolsStore.shouldShowConditionalToolTip) {
    defaultTooltipMsg = await fetchVerbTooltip(verb);
  }
  stores.quickToolsStore.resetTooltipConfig(verb, { defaultTooltipMsg });
};

export const doesToolSupportFileExtension = verbId => {
  const fileExtension = getFileExtension(stores).toLowerCase();
  return fetchFileSelectionParams(verbId).then(fileSelectionParams => {
    if ((fileSelectionParams && fileSelectionParams.acceptExtensions.includes(fileExtension)) || !fileExtension) {
      return true;
    }
    dialogApi.show(localizeComponent(CommonDialog, loadTranslation), {
      title: getFormattedMessage("dialog.convertToPDF.title"),
      message: getFormattedMessage("dialog.convertToPDF.text"),
      confirmLabel: getFormattedMessage("dialog.convertToPDF.convert"),
      cancelLabel: getFormattedMessage("common.cancel"),
      autoFocusButton: "confirm",
      onCancel: () => dialogApi.hide(),
      onConfirm: () => {
        if (!auth.isSignedIn) {
          const url = generateRedirectURL(MEGAVERB_ID_CONVERT);
          auth.signIn({ redirect_uri: url });
        } else {
          // Open Convert Panel in LHP
          const { pathname, search } = window.location;
          const queryParams = new URLSearchParams(search);
          queryParams.set("viewer!megaVerb", "group-convert");
          router.history.push(`${pathname}?${queryParams.toString()}`);
        }
        dialogApi.hide();
      }
    });
    return false;
  });
};

export const isMarkupAnnot = annotType =>
  annotType === "highlight" || annotType === "strikeout" || annotType === "underline";

export const wait = delay => {
  const cancellablePromise = {};
  cancellablePromise.promise = new Promise((resolve, reject) => {
    const timerId = setTimeout(() => resolve(), delay);
    cancellablePromise.cancel = () => {
      reject();
      clearTimeout(timerId);
    };
  });
  return cancellablePromise;
};
